import { GetUserNotificationPreferencesResponse } from '../../UserSettings/state/user-notification-preferences';
import { UserPrivacySettings } from '../../UserSettings/state/user-privacy-settings';
import { Guid } from "guid-typescript";
import { GetSelectListResponse, ListModel } from "shared/interfaces/core-interfaces";
import { Theme } from "constants/enums";
import dayjs from "shared/dayjs";
import yup from "../../../../shared/validation/yupExtensions";

export interface LoginModel {
    email: any,
    password: any,
    rememberMe: boolean
}

export const loginModelSchema = yup.object().shape({
    email: yup.string().required("Email is a required field.").email("Invalid email address."),
    password: yup.string().required("Password is a required field."),
    rememberMe: yup.bool().defined()
})

export interface LoginResponse {
    accessToken: string;
    refreshToken: string;
    tier: string;
    isValidLicense: boolean;
}

export interface LoginData {
    email: any,
    password: any,
    rememberMe: boolean,
}

export interface DemoCorporationInfo {
    id: string;
    name: string;
}

export interface CorporationHelper {
    id: string;
    directoryId: string;
    name: string;
    isDemo: boolean;
    upgradeAvailable: boolean;
}

export interface DirectoryHelper {
    id: string;
    directory: string;
}

export interface MultipleDirectoriesModel {
    corporations: CorporationHelper[];
    unconfiguredCorporations: DirectoryHelper[];
}

export interface LoginResults {
    accessTokenApp: string,
    accessToken: string,
    refreshToken: string,
    setupRequired?: boolean,
    nonDemoCorporationSetupAvailable?: boolean,
    demoCorporation?: DemoCorporationInfo,
    hasMultipleDirectories?: boolean,
    corporationOptions?: MultipleDirectoriesModel | null,
}

export interface ChangeTenantResults {
    accessTokenApp: string,
    tenantId: Guid | null,
    tenantName: string,
    permissionRoleId: string,
    permissionRoleName: string,
    defaultTenantCurrency: string
}

export interface RejectedLoginValue {
    message: string;
    status?: number;
    isMultiCompanyUser?: boolean;
    companies?: GetSelectListResponse<Guid>[];
    defaultCompanyId?: Guid | null;
}

export interface authSliceModel {
    statusCode: string,
    showNotification: boolean,
    message: string,
    isLoading: boolean,
    // Drives the full-screen overlay shown across a tenant switch + reload until boot data settles.
    isSwitchingTenant: boolean,
    error: string,
    hasLoginData: boolean,
    needToChooseCompany: boolean,
    tokenRefreshed: string,
    isTokenRefreshing: boolean,
    tenants: ListModel<Guid>[] | [],
    defaultTenantId?: Guid | null,
    defaultTenantCurrency: string,
    currentCulture: string,
    email: string,
    password: string,
    rememberMe: boolean,
    logTenantId: Guid | null,
    logTenantName: string,
    temporaryLogTenantId: Guid | null,
    /* temporaryLogTenantName: string, */
    corporationName: string,
    corporationDefaultAppLanguage: string,
    userFullName: string,
    userAbv: string,
    userSettings: userSettings | null,
    // WP6 - the channel per notification type for one tenant, plus the tenants the user may
    // switch between. Loaded by the notification preferences tab of the user settings modal.
    notificationPreferences: GetUserNotificationPreferencesResponse | null,
    currentRole: string;
    permissions: string[] | string[],
    currentUrl: string,
    currentPage: string,
    tenantPdfData: TenantPdfData | null,
    theme: Theme | null,
    firstName: string,
    lastName: string,
    gender: string,
    userName: string,
    demoCorporation: DemoCorporationInfo | null
    setupRequired: boolean,
    nonDemoCorporationSetupAvailable: boolean,
    corporationOptions: MultipleDirectoriesModel | null,
    isCorporationEmailServisSetUp: boolean,
    // Both arrive with GetUserSetupData, NOT with the login response - despite the name, that is
    // a second request, and it is in flight at the same time as the rest of the boot fan-out. So
    // for the first moments after a login these are still at their defaults (`false`), and any
    // code that reads them to decide "is the gate up?" has to wait for the setup data first (see
    // login-display.tsx). On a reload they come back with the persisted auth state and are correct
    // from the first render.
    // Drives whether WhatsNewModal opens automatically.
    showWhatsNew: boolean,
    // Drives the blocking TermsGateModal.
    termsAcceptanceRequired: boolean,
}

export interface TenantPdfData {
    logo: string;
    tenantName: string;
    tenantCountry: string;
    tenantAbbreviation: string;
    address: string;
    zipCode: string;
    city: string;
    country: string;
    telephoneNumber: string;
    web: string;
    email: string;
    registerNumber: string;
    taxNumber: string;
    ceo: string;
    bankName?: string;
    iban?: string;
    swift?: string;
    accountNumber?: string;
}

export interface UserSettingsUpdateRequest {
    zoneName: string | null,
    dateFormat: string | null,
    timeFormat: string | null,
    culture: string | null
}

// One legacy email-notification flag (role-filtered by the backend).
// categoryId 0 = Tasks; otherwise a request-type id.
//
// Superseded by the per-tenant channel choice in features/Core/UserSettings/state/user-notification-preferences.ts
// (WP6). Nothing writes these from the UI any more, but GET still returns them and the backend still
// reads them as the default channel for a user who has never saved their preferences - which is what
// made WP6 a rework of this rather than a reset of it.
export interface EmailNotificationSetting {
    categoryId: number,
    label: string,
    isTask: boolean,
    enabled: boolean
}

export const UserSettingsUpdateRequestScheme = yup.object().shape({
    zoneName: yup.string().notOneOf([null], "Time zone is required.").required("Time zone is required.").min(1, "Time zone is required.").nullable(),
    dateFormat: yup.string().notOneOf([null], "Date format is required.").required("Date format is required.").min(1, "Date format is required.").nullable(),
    timeFormat: yup.string().notOneOf([null], "Time format is required.").required("Time format is required.").min(1, "Time format is required.").nullable(),
    culture: yup.string().notOneOf([null], "Language is required.").required("Language is required.").min(1, "Language is required.").nullable(),
})

export interface UserSettingsUpdateResponse {
    employeeId: string
}

export interface routePage {
    currentUrl: string,
    currentPage: string,
}

export interface userSettings {
    zoneName: string,
    dateFormat: string,
    timeFormat: string,
    culture: string | "en",
    cultures: ListModel<string>[],
    dateFormats: ListModel<string>[],
    timeFormats: ListModel<string>[],
    zoneNames: ListModel<string>[],
    employeeId: string,
    // Role-filtered email-notification toggles (Tasks + accessible request types).
    emailNotifications?: EmailNotificationSetting[],
    // WP10 - what colleagues may be told about this user. Read here with the rest of their settings;
    // saved on its own through api/employee/settings/privacy/update. Optional because the setup-data
    // response, which fills these settings at login, does not carry it - the Privacy tab re-reads
    // api/employee/settings/get when the modal opens.
    privacy?: UserPrivacySettings,
}

export interface userSetup {
    employeeSettings: userSettings | null,
    tenantId: Guid | null,
    tenantName: string,
    corporationName: string,
    corporationDefaultAppLanguage: string,
    employeeFullName: string,
    employeeAbv: string,
    tenants: ListModel<Guid>[] | [],
    currentRole: string,
    defaultTenantCurrency: string,
    isCorporationEmailServisSetUp: boolean,
    // True when a published patch-notes release is newer than the one this user has
    // acknowledged. Comes from the login payload so no extra request is needed to decide
    // whether to show the "What's New" modal.
    showWhatsNew: boolean,
    // True until the user has accepted the terms version currently in force.
    termsAcceptanceRequired: boolean,
}

export interface refreshTokenModel {
    accessTokenApp: string,
    accessToken: string,
    refreshToken: string,
    tenantId: Guid
}

export interface decodedToken {
    corporationId: Guid,
    employeeId: Guid,
    permissionRoleId: Guid,
    tenantId: Guid,
    aud: string,
    exp: number,
    iss: string,
    jti: string,
    permission: [],
    sub: Guid,
    valid: string,
    firstname: string,
    lastname: string,
    gender: string,
    username: string
}


export interface ChangeTenantModel {
    tenantId: Guid | null,
}

export interface InitialSetupCreateDataResponse {
    countries: ListModel<number>[],
    genders: ListModel<number>[],
    dateFormats: ListModel<string>[],
    timeFormats: ListModel<string>[],
    supportedLanguages: ListModel<string>[],
}

export interface InitialSetupForm {
    name: string,
    abbreviation: string,
    countryId: number,
    generalMail: string,
    culture: string,
    zoneName?: string,
    dateFormat: string,
    timeFormat: string,
    latitude?: number | undefined,
    longitude?: number | undefined,
    city: string,
    zipCode: string,
    streetAndNumber: string,
    employeeCountryId: number,
    firstName: string,
    lastName: string,
    employeeAbbreviation: string,
    dateOfBirth: Date,
    genderId: number,
    place: string,
    postalCode: string,
    employeeStreetAndNumber: string,
}

export const initialCreateFirstTenantForm: InitialSetupForm = {
    name: '',
    abbreviation: '',
    countryId: 0,
    generalMail: '',
    culture: 'en',
    zoneName: '',
    dateFormat: "DD.MM.YYYY",
    timeFormat: "HH:mm",
    latitude: undefined,
    longitude: undefined,
    city: '',
    zipCode: '',
    streetAndNumber: '',
    employeeCountryId: 0,
    firstName: '',
    lastName: '',
    employeeAbbreviation: '',
    dateOfBirth: new Date(),
    genderId: 0,
    place: '',
    postalCode: '',
    employeeStreetAndNumber: ''
};

export interface InitialSetupRequest extends InitialSetupForm {
    currency?: string,
    locationName?: string,
    state?: string | undefined,
    email: string,
}

export interface InitialSetupResponse {
    appAccessToken: string,
    message: string
}

export const initialCreateFirstTenantRequest: InitialSetupRequest = {
    ...initialCreateFirstTenantForm,
    currency: '',
    locationName: '',
    state: undefined,
    email: ''
};

export const InitialSetupRequestScheme: yup.ObjectSchema<InitialSetupForm> = yup.object().shape({
    name: yup.string().required("Name is required."),
    abbreviation: yup.string().max(3, "Abbreviation must be at most 3 characters.").required("Abbreviation is required."),
    generalMail: yup.string().email("Main email must be a valid email address.").required("Main email is required."),
    culture: yup.string().required("Default application language is required."),
    zoneName: yup.string().optional(),
    dateFormat: yup.string().min(1, "Date format is required.").required("Date format is required."),
    timeFormat: yup.string().min(1, "Time format is required.").required("Time format is required."),
    latitude: yup.number().optional(),
    longitude: yup.number().optional(),
    city: yup.string().noLeadingSpace().required("City is required."),
    zipCode: yup.string().noLeadingSpace().required("Zip code is required."),
    streetAndNumber: yup.string().noLeadingSpace().required("Street and number are required."),
    countryId: yup.number().positive("Country is required.").integer("Country is required.").required("Country is required."),
    employeeCountryId: yup.number().positive("Country is required.").integer("Country is required.").required("Country is required."),
    firstName: yup.string().noLeadingSpace().required("First name is required."),
    lastName: yup.string().noLeadingSpace().required("Last name is required."),
    employeeAbbreviation: yup.string().max(3, "Abbreviation must be at most 3 characters.").required("Abbreviation is required."),
    dateOfBirth: yup.date().typeError("Please type valid date.").min(dayjs().add(-100, 'year').toDate(), "Please insert valid date.")
        .max(dayjs().endOf('day').toDate(), "Please insert valid date.")
        .required('Date of birth is required.'),
    genderId: yup.number().positive("Gender is required.").integer("Gender is required.").required("Gender is required."),
    place: yup.string().noLeadingSpace().required("City is required."),
    postalCode: yup.string().noLeadingSpace().required("Zip code is required."),
    employeeStreetAndNumber: yup.string().noLeadingSpace().required("Street and number are required."),
});